import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { getCaptcha } from "../../state/User";
import { login, register } from "../../apis/firebase";
import Header from "../utils/Header";
import { trackEvent } from "../helpers";
import Loading from "../utils/Loading";
import Center from "../utils/Center";

enum REG_STATE {
  IDLE,
  IN_PROGRESS,
  ERROR,
  SUCCESS,
  TO_THE_DEMO_VERSION,
}

export default function Register() {
  const [formEnabled, enableForm] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [isCaptchaValueCorrect, setCaptchaValue] = useState(null);
  const [regState, setRegState] = useState<REG_STATE>(REG_STATE.IDLE);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCaptcha()
      .then(setCaptcha)
      .catch(() => {
        setCaptcha({});
        setCaptchaValue(true);
      });
  }, []);

  useEffect(() => {
    const ready =
      username !== "" &&
      password !== "" &&
      captcha &&
      isCaptchaValueCorrect &&
      terms;

    enableForm(ready);
    setError(null);
  }, [username, password, captcha, isCaptchaValueCorrect, terms]);

  function renderInputField(
    type: string,
    placeholder: string,
    onUpdate: Function,
    autoFocus = false
  ) {
    return (
      <input
        type={type}
        placeholder={placeholder}
        onChange={(e) => onUpdate(e.target.value)}
        autoFocus={autoFocus}
        disabled={regState === REG_STATE.IN_PROGRESS}
      />
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    try {
      setRegState(REG_STATE.IN_PROGRESS);
      await register({ username, password: password });
      setRegState(REG_STATE.SUCCESS);
    } catch (err) {
      console.error(err);
      setError(err);
      setRegState(REG_STATE.ERROR);
    }
  }

  if (regState === REG_STATE.SUCCESS) {
    return (
      <div className="layout-narrow mt3">
        <h2 className="tac mb1">
          Registration successful! Welcome to Contempas.
        </h2>
        <hr />
        <Link to="/login" className="primary">
          To your content compass 👉
        </Link>
      </div>
    );
  }

  if (regState === REG_STATE.TO_THE_DEMO_VERSION) {
    return (
      <div className="layout-narrow mt3">
        <p className="tac mb1 pt2">Logging in. Please wait.</p>
        <Center width={50}>
          <Loading />
        </Center>
      </div>
    );
  }

  return (
    <div className="layout-narrow mt3 pt1">
      <div className="mb2">
        <p className="tac mb05">
          Contempas will help you automate your social media presence. It will
          prepare and schedule your media posts, podcast episodes or marketing
          campaigns.
        </p>
        <p className="tac fz12">
          Click the button below and use the system as a demo user:
          <span className="block tac mt05">
            <button
              className="reset small"
              onClick={async () => {
                trackEvent("demo_version_login");
                setRegState(REG_STATE.TO_THE_DEMO_VERSION);
                await login({
                  username: "demo@contempas.com",
                  password: "testtest",
                });
                history.push("/");
              }}
            >
              Take me to the demo version
            </button>
          </span>
        </p>
      </div>
      <hr />
      <h1 className="tac">Create new account</h1>
      <form onSubmit={onSubmit} className="auth p1">
        {renderInputField("text", "email", setUsername, true)}
        {renderInputField("password", "password", setPassword)}
        <label className="block pt1 pb1">
          <input
            type="checkbox"
            value="yes"
            onChange={(e) => setTerms(e.target.checked)}
          />{" "}
          I agree with Contempas's{" "}
          <a href="/terms" target="_blank">
            Terms & Condition
          </a>
          .
        </label>
        {captcha && captcha.data ? (
          <div className="grid2">
            <div dangerouslySetInnerHTML={{ __html: captcha.data }} />
            {renderInputField("text", "← those letters", (value) => {
              setCaptchaValue(value === captcha.text);
            })}
          </div>
        ) : null}
        <button
          type="submit"
          className="primary"
          disabled={!formEnabled || regState === REG_STATE.IN_PROGRESS}
        >
          Register
        </button>
        <RegistrationError error={error} />
      </form>
      <hr />
      <p className="tac mt1 mb1">
        You already have an account?
        <br />
        Log in <Link to="/login">here</Link>.
      </p>
    </div>
  );
}

function RegistrationError({
  error,
}: {
  error: { code: string; message: string } | null;
}) {
  let message = "Ops! The registration process failed. Please try again later.";

  if (error !== null) {
    if (error.code === "auth/invalid-email") {
      message = "Invalid email. Please enter a correct one.";
    } else if (error.code === "auth/weak-password") {
      message = error.message + ".";
    } else if (error.code === "auth/email-already-in-use") {
      message = "The email already exists.";
    } else if (error.code === "INVALID_CAPTCHA") {
      message = "Invalid reCAPTCHA.";
    }
    return <div className="error mt1">{message}</div>;
  }
  return null;
}
